import { useCallback, useState } from "react";

import { type HistoryActionKey } from "utilities/historyState/actionWithHistory.utility";
import { useActionWithHistory } from "utilities/historyState/useActionWithHistory";

import { type SavedViewsAndFiltersSettingsState } from "entity/filters/SavedViewsAndFiltersList/SavedViewsAndFiltersCard/SavedViewsAndFiltersCard.api.types";

/** The settings panel's open sub-modes — the full settings state minus the closed case. */
export type SavedViewsAndFiltersSubMode = Exclude<SavedViewsAndFiltersSettingsState, "closed">;

export interface IUseSavedViewsSettingsStateResult {
    /**
     * Combined panel state, derived from history (open vs. closed) and React state
     * (the open sub-mode). Read this exactly like the former `gridSettingsState`.
     */
    settingsState: SavedViewsAndFiltersSettingsState;
    /**
     * Transition the panel. Passing "closed" pops the history entry (when open);
     * passing any sub-mode opens the panel — pushing a history entry if it wasn't
     * already open — and selects that sub-mode.
     */
    setSettingsState: (next: SavedViewsAndFiltersSettingsState) => void;
}

/**
 * Single source of truth for the Manage Saved Views / Saved Filters panel.
 *
 * Open/closed is owned solely by history state (so the mobile back button
 * dismisses the panel), while the open sub-mode ("open" | "editing" | ...) is
 * ordinary React state. The two dimensions are orthogonal, so `settingsState` is
 * derived with no bridge or reconciliation effect — this replaces the former
 * pattern of mirroring open/closed into both a consumer's React state and history.
 *
 * Class components can't call this directly; use
 * {@link withSavedViewsSettingsState} to inject the result as props.
 */
export function useSavedViewsSettingsState(
    actionKey: HistoryActionKey,
): IUseSavedViewsSettingsStateResult {
    const { isOpen, open, close } = useActionWithHistory(actionKey);
    const [subMode, setSubMode] = useState<SavedViewsAndFiltersSubMode>("open");

    const settingsState: SavedViewsAndFiltersSettingsState = isOpen ? subMode : "closed";

    const setSettingsState = useCallback(
        (next: SavedViewsAndFiltersSettingsState) => {
            if (next === "closed") {
                // Reset so the next open starts at the base view; only pop history when actually open.
                setSubMode("open");
                if (isOpen) {
                    close();
                }
                return;
            }
            setSubMode(next);
            if (!isOpen) {
                open();
            }
        },
        [isOpen, open, close],
    );

    return { settingsState, setSettingsState };
}
